/**
 * Tools that fan one audit route out into several provider requests. They run
 * as standalone client batches so provider work from one tool has settled
 * before the next heavy tool starts. AEO is included because its optional
 * Tier-3 pass launches five Gemini and five Claude judgments.
 */
export const PROVIDER_INTENSIVE_TOOLS = new Set([
  'aeo',
  'citation',
  'sourceInfluence',
  'promptSov',
])

/** Historical export retained for callers that only need the Perplexity set. */
export const MULTI_PROMPT_PERPLEXITY_TOOLS = new Set([
  'citation',
  'sourceInfluence',
  'promptSov',
])

type KeyedTool = { key: string }

/**
 * Build fixed-size route batches while keeping provider-intensive tools in
 * standalone batches. Non-intensive tools keep their relative order and may
 * share a batch up to `batchSize`.
 */
export function planToolBatches<T extends KeyedTool>(
  tools: readonly T[],
  batchSize: number = 3
): T[][] {
  if (!Number.isInteger(batchSize) || batchSize < 1) {
    throw new RangeError('batchSize must be a positive integer')
  }
  if (tools.length === 0) return []

  const batches: T[][] = []
  let regularBatch: T[] = []

  for (const tool of tools) {
    if (PROVIDER_INTENSIVE_TOOLS.has(tool.key)) {
      if (regularBatch.length > 0) {
        batches.push(regularBatch)
        regularBatch = []
      }
      batches.push([tool])
      continue
    }
    regularBatch.push(tool)
    if (regularBatch.length === batchSize) {
      batches.push(regularBatch)
      regularBatch = []
    }
  }

  if (regularBatch.length > 0) batches.push(regularBatch)
  return batches
}

/** Run each batch after the previous batch has fully settled. */
export async function runSequentialToolBatches<T>(
  batches: readonly (readonly T[])[],
  worker: (item: T) => Promise<unknown>,
  onBatchStart?: (batch: readonly T[], index: number) => void
): Promise<PromiseSettledResult<unknown>[]> {
  const outcomes: PromiseSettledResult<unknown>[] = []
  for (let index = 0; index < batches.length; index++) {
    const batch = batches[index]
    onBatchStart?.(batch, index)
    outcomes.push(...(await Promise.allSettled(batch.map(worker))))
  }
  return outcomes
}
